import {
	Component,
	ComponentInterface,
	h,
	Prop,
	Event,
	EventEmitter,
	Listen,
	Host,
	Element,
} from '@stencil/core';
import { BasketProxy } from '@blk/compare-basket';
import { calculateChildTagName } from '../_helpers/prefixes';
import { IsharesButtonType } from '../_types/button';

export type CompareButtonState = 'add' | 'remove' | 'disabled';

@Component({
	tag: 'ds-add-to-compare-button',
})
export class AddToCompareButton implements ComponentInterface {
	@Prop({ mutable: true }) fundId: number;
	@Prop({ reflect: true, mutable: true }) state: CompareButtonState;
	@Prop({ reflect: true }) color:
		| ''
		| 'white'
		| 'blue'
		| 'purple'
		| 'pink'
		| 'green'
		| 'yellow'
		| 'black';
	@Prop() buttonType: IsharesButtonType = 'primary';
	@Prop({ mutable: true }) addToCompareText = 'ADD TO COMPARE';
	@Prop({ mutable: true }) removeFromCompareText = 'REMOVE';
	@Prop({ mutable: true }) addLinkEvent = 'CTA|add-to-compare'; // This is a sensible default, however the real string should follow a longer format with extra info
	@Prop({ mutable: true }) removeLinkEvent = 'CTA|remove-from-compare'; // This is a sensible default, however the real should follow a longer format with extra info
	@Prop({ mutable: true }) disabledLinkEvent = 'CTA|basket-is-full'; // This is a sensible default, however the real should follow a longer format with extra info
	@Prop() tooManyFundsTooltipHeader: string;
	@Prop() tooManyFundsTooltipBody: string;
	@Event() compareButtonClick: EventEmitter<{
		state: CompareButtonState;
		fundId: number;
	}>;

	@Element() el: HTMLElement;

	componentWillLoad(): void {
		window.DS = window.DS || {};
		window.DS.basketProxy = window.DS.basketProxy || new BasketProxy();

		window.DS.basketProxy
			.exposedMethods()
			.onBasketChange(this.handleBasketChange);
		this.setStateFromBasketProxy();
	}

	setStateFromBasketProxy(): void {
		if (window.DS.basketProxy.exposedMethods().containsFund(this.fundId)) {
			this.state = 'remove';
		} else if (window.DS.basketProxy.exposedMethods().isBasketFull()) {
			this.state = 'disabled';
		} else {
			this.state = 'add';
		}
	}

	handleBasketChange = () => {
		this.setStateFromBasketProxy();
	};

	@Listen('buttonClick')
	handleButtonClick(): void {
		this.compareButtonClick.emit({
			state: this.state,
			fundId: this.fundId,
		});

		switch (this.state) {
			case 'add':
				window.DS.basketProxy.exposedMethods().addFundToBasket(this.fundId);
				break;
			case 'remove':
				window.DS.basketProxy.exposedMethods().removeFromBasket(this.fundId);
				break;
		}
	}

	render() {
		const Tooltip = calculateChildTagName(this.el, 'ds-tooltip');
		const Button = calculateChildTagName(this.el, 'ds-button');

		return (
			<Host>
				{this.state === 'disabled' ? (
					<Tooltip
						disabled={false}
						fauxDisabled={true}
						header={this.tooManyFundsTooltipHeader}
						content={this.tooManyFundsTooltipBody}
						mode="caution"
						ariaLabel={`${this.addToCompareText} ${this.tooManyFundsTooltipHeader}, ${this.tooManyFundsTooltipBody}`}
						button-type="primary-button"
						button-text={this.addToCompareText}
						button-color={this.color}
						button-link-event={this.disabledLinkEvent}
					/>
				) : (
					<Button
						type={this.state === 'remove' ? 'primary' : this.buttonType}
						compact={true}
						color={this.state === 'remove' ? 'black' : this.color}
						accessibilityLabel={
							this.state === 'remove'
								? this.removeFromCompareText
								: this.addToCompareText
						}
						data-link-event={
							this.state === 'remove' ? this.removeLinkEvent : this.addLinkEvent
						}
					>
						{this.state === 'remove'
							? [
									<span>{this.removeFromCompareText}</span>,
									<i class="fas fa-trash" aria-hidden="true" />,
							  ]
							: this.addToCompareText}
					</Button>
				)}
			</Host>
		);
	}
}
